import React, { useState, useEffect, useMemo } from "react";
import { useCMTagList } from "api/api";
import moment, { Moment } from "moment";
import {
  XAxis,
  YAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
  CartesianGrid,
  ReferenceArea,
  LineChart,
  Line,
} from "recharts";
import Card from "components/cards/Card";
import Checkbox from "components/basic/Input/Checkbox";
import Dropdown from "components/basic/Dropdown";
import { Energy_Graph } from "model/Datatypes";

const minEnergyValue = 0; //minimum value of energy to show data

type Props = {
  hideControls?: boolean;
  height?: string;
  tags: Energy_Graph["tags"];
};

const EnergyGraphComp = ({ hideControls, tags }: Props) => {
  const { energyDayData, energyMonthData, loading } = useEnergyData(tags);

  //data for reference area calculate:
  const noDataRange = useMemo(() => {
    let rangeStart: string | null = null;
    let rangeEnd: string | null = null;
    for (let i = 0; i < energyMonthData.length; i++) {
      const point = energyMonthData[i];
      const hasData =
        point.heating > minEnergyValue ||
        point.energy > minEnergyValue ||
        point.cooling > minEnergyValue;
      if (!hasData) {
        if (!rangeStart) rangeStart = point.name;
        else {
          const nextPoint = energyMonthData[i + 1]?.name;
          rangeEnd = nextPoint || point.name;
        }
      }
    }
    if (rangeStart && rangeEnd) return { rangeStart, rangeEnd };
    else return null;
  }, [energyMonthData]);

  const [displayEnergy, setDisplayEnergy] = useState(true);
  const [displayHeating, setDisplayHeating] = useState(true);
  const [displayCooling, setDisplayCooling] = useState(true);
  const [hoveredBar, sethoveredBar] = useState<string>("");

  const [displayType, setDisplayType] = useState<"monthly_sum" | "graph">("monthly_sum");

  const [selectedMonth, setselectedMonth] = useState<Moment | null>(null);
  const [displayedDayData, setDisplayedDayData] = useState<EnergyData[]>([]);
  const getDataInMonth = (time: Moment) => {
    return energyDayData.filter((p) => p.timeStamp.isSame(time, "month"));
  };
  const now = useMemo(() => moment(), []);
  const startTime = useMemo(() => moment().subtract(12, "month"), []);

  // const filteredEnergyMonthData = energyMonthData.filter(
  //   (e) => e.cooling !== 0 && e.energy !== 0 && e.heating !== 0 && e.hotWater !== 0
  // );

  const renderFullBarGraph = () => {
    return (
      <BarChart
        className="cursor-pointer w-full"
        data={energyMonthData}
        barCategoryGap={"20%"}
        barGap={2}
        onClick={(e) => {
          const clickedPoint = e?.activePayload?.pop().payload as EnergyData | undefined;
          if (clickedPoint) {
            if (hoveredBar) {
              hoveredBar !== "heating" && setDisplayHeating(false);
              hoveredBar !== "cooling" && setDisplayCooling(false);
              hoveredBar !== "energy" && setDisplayEnergy(false);
            }
            setDisplayedDayData(getDataInMonth(clickedPoint.timeStamp));
            setselectedMonth(clickedPoint.timeStamp);
            sethoveredBar("");
            setDisplayType("graph");
          }
        }}
      >
        <XAxis
          tick={{ fontSize: "1rem" }}
          dataKey="name"
          // tickFormatter={(val) => moment(val).format(momentFromat)}
        />
        <YAxis tickFormatter={(val) => `${val} MWh`} tick={{ fontSize: "1rem" }} />
        <CartesianGrid vertical={false} />
        <Tooltip
          cursor={{ opacity: 0.1, cursor: "pointer" }}
          formatter={(value, name, entry) => [
            `${typeof value === "number" ? value.toFixed(2) : value}`,
            `${name}`,
          ]}
        />
        <Bar
          hide={displayHeating ? undefined : true}
          className="cursor-pointer"
          dataKey="totalHeating"
          fill="#FF5F45"
          unit="MWh"
          opacity={hoveredBar && hoveredBar !== "heating" ? 0.5 : undefined}
          onMouseEnter={() => {
            sethoveredBar("heating");
          }}
          onMouseLeave={() => {
            if (hoveredBar === "heating") sethoveredBar("");
          }}
        />

        <Bar
          hide={displayCooling ? undefined : true}
          className="cursor-pointer"
          dataKey="cooling"
          fill="#4DA5FE"
          unit="MWh"
          opacity={hoveredBar && hoveredBar !== "cooling" ? 0.5 : undefined}
          onMouseEnter={() => {
            sethoveredBar("cooling");
          }}
          onMouseLeave={() => {
            if (hoveredBar === "cooling") sethoveredBar("");
          }}
        />

        <Bar
          hide={displayEnergy ? undefined : true}
          className="cursor-pointer"
          dataKey="energy"
          fill="#6A6A6A"
          opacity={hoveredBar && hoveredBar !== "energy" ? 0.5 : undefined}
          unit="MWh"
          onMouseEnter={() => {
            sethoveredBar("energy");
          }}
          onMouseLeave={() => {
            if (hoveredBar === "energy") sethoveredBar("");
          }}
        />

        {noDataRange && (
          <ReferenceArea
            x1={noDataRange.rangeStart}
            x2={noDataRange.rangeEnd}
            className="relative"
            strokeOpacity={0.5}
            fill="#F4F4F4"
            // @ts-ignore
          ></ReferenceArea>
        )}
      </BarChart>
    );
  };

  const renderLineGraph = () => {
    return (
      <LineChart data={displayedDayData}>
        <XAxis
          label={{}}
          allowDataOverflow
          domain={["dataMin", "dataMax"]}
          type="number"
          dataKey="timeVal"
          tickFormatter={(val) => `${moment(val * 1000).format("DD/MM")}`}
          tick={{ fontSize: "1rem" }}
        />
        <YAxis tickFormatter={(val) => `${val} kWh`} tick={{ fontSize: "1rem" }} />
        <CartesianGrid vertical={false} />
        <Tooltip
          cursor={{ opacity: 0.1 }}
          //@ts-ignore
          content={<CustomTooltip />}
        />

        <Line
          type="monotone"
          strokeWidth={3}
          hide={displayHeating ? undefined : true}
          dot={false}
          dataKey="totalHeating"
          stroke="#FF5F45"
          unit="kWh"
        />
        <Line
          type="monotone"
          strokeWidth={3}
          dot={false}
          hide={displayCooling ? undefined : true}
          dataKey="cooling"
          stroke="#4DA5FE"
          unit="kWh"
        />
        <Line
          type="monotone"
          hide={displayEnergy ? undefined : true}
          strokeWidth={3}
          dot={false}
          dataKey="energy"
          stroke="#6A6A6A"
          unit="kWh"
        />
      </LineChart>
    );
  };

  const renderGraph = () => {
    if (displayType === "monthly_sum")
      return <ResponsiveContainer>{renderFullBarGraph()}</ResponsiveContainer>;
    else return <ResponsiveContainer>{renderLineGraph()}</ResponsiveContainer>;
  };

  const renderMonthNavigator = () => {
    return (
      <div className="absolute top-0 mt-2 left-0 w-full flex items-center justify-center bg-white border-gray-200 ">
        {selectedMonth && selectedMonth.isAfter(startTime, "month") && (
          <button
            onClick={() => {
              if (selectedMonth) {
                const prevMonth = selectedMonth.clone().add(-1, "month");
                setDisplayedDayData(getDataInMonth(prevMonth));
                setselectedMonth(prevMonth);
              }
            }}
            className={` focus:outline-none h-4 w-4`}
          >
            <ArrowBack />
          </button>
        )}
        <span className="font-medium text-base 2xl:text-3xl mx-2">
          {selectedMonth?.format("MMMM")}
        </span>
        {selectedMonth && selectedMonth.isBefore(now, "month") && (
          <button
            onClick={() => {
              if (selectedMonth) {
                const nextMonth = selectedMonth.clone().add(1, "month");
                setDisplayedDayData(getDataInMonth(nextMonth));
                setselectedMonth(nextMonth);
              }
            }}
            className={` focus:outline-none h-4 w-4`}
          >
            <ArrowForward />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full">
      <Card
        customFoot={
          <Dropdown
            flipped={true}
            options={[
              { id: "monthly_sum", display: "All months" },
              { id: "graph", display: "Details" },
            ]}
            selectedID={displayType}
            onSelect={(option) => {
              if (option.id === "graph") {
                const month = now.clone();
                setDisplayedDayData(getDataInMonth(month));
                setselectedMonth(month);
              }
              //@ts-ignore
              setDisplayType(option.id);
            }}
          />
        }
        size="full"
        loading={loading}
      >
        <div className="h-full w-full flex relative">
          {/* <div className="text-lg 2xl:text-3xl text-gray-600 absolute top-2 2xl:top-12 left-6 z-10 2xl:left-28 font-medium">
            Energy Supplied to building
          </div> */}
          <div className="flex-grow px-4 pt-10 relative">
            {renderGraph()}
            {displayType === "graph" && renderMonthNavigator()}
          </div>
          {!hideControls && (
            <div className="w-40 2xl:w-80 bg-gray-200 px-4 py-4">
              <Checkbox
                className="my-1 2xl:my-5"
                checked={displayEnergy}
                color={"#6A6A6A"}
                onChange={(newState) => {
                  setDisplayEnergy(newState);
                }}
              >
                <div className="ml-2 2xl:text-3xl text-gray-700">Energy used</div>
              </Checkbox>
              <Checkbox
                className="my-1 2xl:my-5"
                checked={displayHeating}
                color={"#FF5F45"}
                onChange={(newState) => {
                  setDisplayHeating(newState);
                }}
              >
                <div className="ml-2 2xl:text-3xl text-gray-700">Heating delivered</div>
              </Checkbox>
              <Checkbox
                className="my-1 2xl:my-5"
                checked={displayCooling}
                color={"#4DA5FE"}
                onChange={(newState) => {
                  setDisplayCooling(newState);
                }}
              >
                <div className="ml-2 2xl:text-3xl text-gray-700">Cooling delivered</div>
              </Checkbox>
              <div className="my-4">
                <hr />
              </div>
              {displayType === "graph" && (
                <button
                  onClick={() => {
                    setDisplayType("monthly_sum");
                    setselectedMonth(null);
                    setDisplayHeating(true);
                    setDisplayEnergy(true);
                    setDisplayCooling(true);
                  }}
                  className={`bg-white px-2 w-full focus:outline-none border border-gray-200 shadow text-xs 2xl:text-3xl 2xl:px-4 2xl:py-2`}
                >
                  Back to monthly view
                </button>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default EnergyGraphComp;

type EnergyData = {
  name: string; //Jan/feb/....
  timeStamp: Moment;
  timeVal: number;
  totalHeating: number;
  hotWater: number;
  heating: number;
  heating_ref?: number;
  cooling: number;
  cooling_ref?: number;
  energy: number;
  energy_ref?: number;
};

const CustomTooltip: React.FC<{ active: boolean; payload: any[]; label: any }> = ({
  active,
  payload,
  label,
}) => {
  if (active) {
    return (
      <div className="bg-white shadow-md border border-gray-400 p-4">
        <p className="text-md font-bold">{`${moment(label * 1000).format("DD/MM YY")}`}</p>
        {payload.map((pload) => {
          const displayname =
            pload.name === "cooling"
              ? "Cooling delivered"
              : pload.name === "totalHeating"
              ? "Heating delivered"
              : "Energy used";
          const val = typeof pload.value === "number" ? pload.value.toFixed(2) : pload.value;
          return (
            <div className="w-full flex" style={{ color: pload.color }}>
              <span className="flex-grow pr-6 font-medium">{displayname}</span>
              <span>{`${val} kWh`}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

const useEnergyData = (tags: Energy_Graph["tags"]) => {
  const energyTags = useMemo(
    () => [tags.heating, tags.cooling, tags.hot_water, tags.electricity],
    [tags]
  );

  const timeOptions = useMemo(
    () => ({ from: moment().subtract(6, "months").valueOf(), to: moment().valueOf() }),
    []
  );

  const { cmData: cmEnergyData, loading } = useCMTagList(
    energyTags,
    timeOptions.from,
    timeOptions.to,
    "DAYS",
    true
  );

  const [energyDayData, setEnergyDayData] = useState<EnergyData[]>([]);
  const [energyMonthData, setEnergyMonthData] = useState<EnergyData[]>([]);

  //get day values
  useEffect(() => {
    const energyData: EnergyData[] = [];
    const heatingVals = cmEnergyData?.find((res) => res.name === tags.heating)?.points;
    const hotWaterVals = cmEnergyData?.find((res) => res.name === tags.hot_water)?.points;
    const coolingVals = cmEnergyData?.find((res) => res.name === tags.cooling)?.points;
    const electricityVals = cmEnergyData?.find((res) => res.name === tags.electricity)?.points;
    const now = moment();

    const prevTotal = {
      hotWater: 0,
      heating: 0,
      cooling: 0,
      energy: 0,
    };

    for (let i = 365; i >= 0; i--) {
      const momentI = moment().subtract(i, "days");

      const curCooling = getAvg(
        coolingVals?.filter((p) => moment(p.time).isSame(momentI, "day"))
      );
      const coolingChange = getChange(curCooling, prevTotal.cooling);
      if (curCooling > 0) prevTotal.cooling = curCooling;

      const curHeating = getAvg(
        heatingVals?.filter((p) => moment(p.time).isSame(momentI, "day"))
      );
      const heatingChange = getChange(curHeating, prevTotal.heating);
      if (curHeating > 0) prevTotal.heating = curHeating;

      const curHotWater = getAvg(
        hotWaterVals?.filter((p) => moment(p.time).isSame(momentI, "day"))
      );
      const hotWaterChange = getChange(curHotWater, prevTotal.hotWater);
      if (curHotWater > 0) prevTotal.hotWater = curHotWater;

      const totalHeatingChange = heatingChange + hotWaterChange;

      const curEnergy = getAvg(
        electricityVals?.filter((p) => moment(p.time).isSame(momentI, "day"))
      );
      const energyChgange = getChange(curEnergy, prevTotal.energy);
      if (curEnergy > 0) prevTotal.energy = curEnergy;

      let name = momentI.format("DD/MM");
      if (!momentI.isSame(now, "year")) name += ` ${momentI.format("YY")}`;

      energyData.push({
        name,
        totalHeating: totalHeatingChange * 1000,
        heating: heatingChange * 1000,
        hotWater: hotWaterChange * 1000,
        cooling: coolingChange * 1000,
        energy: energyChgange * 1000,
        timeStamp: momentI,
        timeVal: momentI.unix(),
      });
    }
    setEnergyDayData(energyData);
  }, [cmEnergyData, tags]);

  //get month values by taking daily changes and adding them up:
  useEffect(() => {
    const energyData: EnergyData[] = [];
    const now = moment();
    for (let i = 12; i >= 0; i--) {
      const momentI = moment().subtract(i, "months");

      const allChanges = energyDayData.filter((p) => p.timeStamp.isSame(momentI, "month"));
      const cooling = allChanges.reduce((prev, cur) => cur.cooling + prev, 0);
      const heating = allChanges.reduce((prev, cur) => cur.heating + prev, 0);
      const hotWater = allChanges.reduce((prev, cur) => cur.hotWater + prev, 0);
      const totalHeating = allChanges.reduce((prev, cur) => cur.totalHeating + prev, 0);
      const energy = allChanges.reduce((prev, cur) => cur.energy + prev, 0);
      let name = momentI.format("MMM");

      if (!momentI.isSame(now, "year")) name += ` ${momentI.format("YY")}`;
      energyData.push({
        name,
        totalHeating: totalHeating === 0 ? 0 : totalHeating / 1000,
        heating: heating === 0 ? 0 : heating / 1000,
        hotWater: hotWater === 0 ? 0 : hotWater / 1000,
        cooling: cooling === 0 ? 0 : cooling / 1000,
        energy: energy === 0 ? 0 : energy / 1000,
        timeStamp: momentI,
        timeVal: momentI.unix(),
      });
    }
    setEnergyMonthData(energyData);
  }, [energyDayData]);

  return { energyDayData, energyMonthData, loading };
};

const getChange = (curVal: number, prevVal: number) => {
  if (prevVal === 0) return 0;
  const change = curVal - prevVal;
  if (change < 0 || change > 15) return 0;
  else return change;
};

const getAvg = (points: { time: number; value: number }[] | undefined) => {
  if (!points || points.length === 0) return 0;
  const total = points.reduce((prev, cur) => prev + cur.value, 0);
  return total / points.length;
};

const ArrowForward = () => {
  return (
    <svg
      fill="currentColor"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <path fill="none" d="M24 24H0V0h24v24z" opacity="0.87"></path>
      <path d="M7.38 21.01c.49.49 1.28.49 1.77 0l8.31-8.31a.996.996 0 000-1.41L9.15 2.98c-.49-.49-1.28-.49-1.77 0s-.49 1.28 0 1.77L14.62 12l-7.25 7.25c-.48.48-.48 1.28.01 1.76z"></path>
    </svg>
  );
};
const ArrowBack = () => {
  return (
    <svg
      fill="currentColor"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <path fill="none" d="M0 0h24v24H0V0z" opacity="0.87"></path>
      <path d="M16.62 2.99a1.25 1.25 0 00-1.77 0L6.54 11.3a.996.996 0 000 1.41l8.31 8.31c.49.49 1.28.49 1.77 0s.49-1.28 0-1.77L9.38 12l7.25-7.25c.48-.48.48-1.28-.01-1.76z"></path>
    </svg>
  );
};
